import React from 'react'

import { Col, Hr, Row, Spacer } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { MediaManager } from 'src/components/MediaContentsEditor/MediaManager'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { useSetLocaleWiseMediaOpt } from 'src/hooks/useSetLocaleWiseMediaOpt'
import { FreeTextDisplayType, FreeTextItemDTO } from 'src/models/dto/items/FreeTextItemDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorNumberInput,
    ItemEditorSelectInput,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { ItemEntityService } from 'src/services/EntityServices/ItemEntityService'

const displayTypeDisplayNames = new Map<FreeTextDisplayType, string>([
    [FreeTextDisplayType.SingleLine, 'Single line'],
    [FreeTextDisplayType.MultiLine, 'Multiple lines'],
])

export const FreeTextItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError = false,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<FreeTextItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    const { matches } = useBreakpoints()

    const flexItemWidth = matches.s ? '100%' : 'default'

    const FREE_TEXT_DISPLAY_TYPE_FLEX_BASIS = matches.s ? '100%' : '300px'

    const setLocaleWiseMediaOpt = useSetLocaleWiseMediaOpt(itemDTO)

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={ItemEntityService}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            >
                <Col flex={`3 0 ${matches.s ? '100%' : '200px'}`}>
                    <ItemEditorNumberInput
                        dataTestId={'character-limit'}
                        inputId={'response-character-limit'}
                        itemId={itemDTO.id}
                        disabled={!isDefaultLocale(locale) || editDisabled}
                        labelText='Response Character Limit'
                        value={itemDTO.characterLimit === 0 ? null : itemDTO.characterLimit}
                        placeholder='500'
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.characterLimit ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        setValue={(nextValue: number | null) => {
                            ItemEntityService.update({
                                ...itemDTO,
                                characterLimit: nextValue ?? 0,
                            } as FreeTextItemDTO)
                        }}
                    />
                </Col>
                <Col flex={`2 0 ${FREE_TEXT_DISPLAY_TYPE_FLEX_BASIS}`}>
                    <ItemEditorSelectInput
                        dataTestId={'free-text-display-type'}
                        valueToDisplayNames={displayTypeDisplayNames}
                        disabled={!isDefaultLocale(locale) || editDisabled}
                        inputId={'free-text-display-type'}
                        itemId={itemDTO.id}
                        labelText={'Item Layout'}
                        value={itemDTO.freeTextDisplayType}
                        setValue={(nextValue: FreeTextDisplayType) => {
                            ItemEntityService.update({
                                ...itemDTO,
                                freeTextDisplayType: nextValue,
                            } as FreeTextItemDTO)
                        }}
                    />
                </Col>
            </ItemEditorBasicInfo>
            <Spacer height={'S200'} />
            <Row alignItems={'center'}>
                <Col flexGrow={1} width={flexItemWidth}>
                    <ItemEditorTextInput
                        dataTestId={'stimulus'}
                        disabled={editDisabled}
                        inputId={'stimulus'}
                        value={itemDTO.questionI18N[locale] ?? ''}
                        placeholder={'Enter the stimulus question'}
                        itemId={itemDTO.id}
                        setValue={(nextValue: string) => {
                            ItemEntityService.update({
                                ...itemDTO,
                                questionI18N: {
                                    ...itemDTO.questionI18N,
                                    [locale]: nextValue,
                                },
                            } as FreeTextItemDTO)
                        }}
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.questionI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        labelText={'Stimulus'}
                    />
                </Col>
            </Row>
            <Spacer height='S400' />
            <Hr />
            <MediaManager
                itemId={itemDTO.id}
                locale={locale}
                localeWiseMediaOpt={itemDTO.localeWiseMedia}
                setLocaleWiseMediaOpt={setLocaleWiseMediaOpt}
                disabled={editDisabled}
            />
        </>
    )
}
